import { Component } from '@opten/gin-tonic/helpers/compiler';

@Component({ className: 'js-team-save' })
export class TeamContactComponent {
	// Static business info for VCF (does not affect UI rendering)
	private static readonly ORG_NAME = 'displayactive';
	private static readonly WEBSITE_URL = 'https://www.displayactive.ch';
	constructor(private element: HTMLElement) {
		if (!this.supportsVCardDownload()) {
			this.element.classList.add('hidden');
			return;
		}
		this.element.addEventListener('click', this.onClick);
	}

	private onClick = (ev: Event) => {
		ev.preventDefault();

		const container =
			this.element.closest('.c-team-card__content') ||
			this.element.parentElement ||
			document.body;

		const nameInView = (
			container.querySelector('.c-team-card__name')?.textContent || ''
		).trim();
		const data = (container as HTMLElement).dataset || {};
		const fullName = (data.fullnameVcf || nameInView).trim();
		const jobTitle = (
			container.querySelector('.c-team-card__role')?.textContent || ''
		).trim();

		const phoneLink = container.querySelector<HTMLAnchorElement>(
			'.c-team-card__contact--phone'
		);
		const emailLink = container.querySelector<HTMLAnchorElement>(
			'.c-team-card__contact--email'
		);

		const phone = (
			phoneLink?.getAttribute('href') ||
			phoneLink?.textContent ||
			''
		)
			.replace(/^tel:/i, '')
			.trim();
		const email = (
			emailLink?.getAttribute('href') ||
			emailLink?.textContent ||
			''
		)
			.replace(/^mailto:/i, '')
			.trim();

		// Normalize phone to begin with + (e.g., 0041 -> +41) and strip punctuation
		const normalizedPhone = this.normalizePhone(phone);

		const address = {
			street: (data.street || '').trim() || undefined,
			postalCode: (data.postalCode || '').trim() || undefined,
			city: (data.city || '').trim() || undefined,
			country: (data.country || '').trim() || undefined,
		};

		const vcard = this.buildVCard({
			fullName,
			jobTitle,
			phone: normalizedPhone,
			email,
			org: TeamContactComponent.ORG_NAME,
			website: TeamContactComponent.WEBSITE_URL,
			address,
		});

		const filename = this.toSafeFilename(fullName || 'contact') + '.vcf';
		const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
		const url = URL.createObjectURL(blob);

		const a = document.createElement('a');
		a.href = url;
		a.download = filename;
		document.body.appendChild(a);
		a.click();
		requestAnimationFrame(() => {
			URL.revokeObjectURL(url);
			a.remove();
		});
	};

	private supportsVCardDownload(): boolean {
		try {
			const a = document.createElement('a');
			const hasDownload = 'download' in a;
			const hasBlob = typeof Blob !== 'undefined';
			const hasURL =
				typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function';
			return !!(hasDownload && hasBlob && hasURL);
		} catch {
			return false;
		}
	}

	private buildVCard({
		fullName,
		jobTitle,
		phone,
		email,
		org,
		website,
		address,
	}: {
		fullName: string;
		jobTitle?: string;
		phone?: string;
		email?: string;
		org?: string;
		website?: string;
		address?: {
			street?: string;
			postalCode?: string;
			city?: string;
			country?: string;
		};
	}): string {
		const { firstName, lastName } = this.splitName(fullName);
		const lines: string[] = [];
		lines.push('BEGIN:VCARD');
		lines.push('VERSION:3.0');
		// N:Last;First;Middle;Prefix;Suffix
		lines.push(`N:${this.escape(lastName)};${this.escape(firstName)};;;`);
		lines.push(`FN:${this.escape(fullName)}`);
		if (org) lines.push(`ORG:${this.escape(org)}`);
		if (jobTitle) lines.push(`TITLE:${this.escape(jobTitle)}`);
		if (phone) lines.push(`TEL;TYPE=WORK,VOICE:${this.escape(phone)}`);
		if (email) {
			const emailDisplay = `${fullName ? this.escape(fullName) : ''}${
				fullName ? ' ' : ''
			}(${this.escape(email)})`.trim();

			// Standard EMAIL entry
			lines.push(`EMAIL;TYPE=INTERNET,PREF:${this.escape(email)}`);

			// Apple Contacts label to show "Full Name (email)" as display text when supported
			// lines.push(`item1.EMAIL;TYPE=INTERNET:${this.escape(email)}`);
			// lines.push(`item1.X-ABLabel:${emailDisplay}`);
		}
		// if (website) lines.push(`URL:${this.escape(website)}`);
		if (website) lines.push(`URL;TYPE=WORK:${this.escape(website)}`);
		if (
			address &&
			(address.street || address.city || address.postalCode || address.country)
		) {
			// ADR;TYPE=WORK:;;Street;City;Region;PostalCode;Country
			const street = this.escape(address.street || '');
			const city = this.escape(address.city || '');
			const region = '';
			const postal = this.escape(address.postalCode || '');
			const country = this.escape(address.country || '');
			lines.push(
				`ADR;TYPE=WORK:;;${street};${city};${region};${postal};${country}`
			);
		}
		lines.push('END:VCARD');
		// vCard prefers CRLF line endings
		return lines.join('\r\n');
	}

	private normalizePhone(phone: string): string {
		if (!phone) return '';
		let p = phone.trim();
		// Remove common formatting characters
		p = p.replace(/[\s()-]/g, '');
		// If it starts with tel:, strip it (safety if any slipped through)
		p = p.replace(/^tel:/i, '');
		// Convert leading 00 to +
		if (p.startsWith('00')) {
			p = '+' + p.slice(2);
		}
		return p;
	}

	private splitName(fullName: string): { firstName: string; lastName: string } {
		const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) return { firstName: '', lastName: '' };
		if (parts.length === 1) return { firstName: parts[0], lastName: '' };
		const lastName = parts.pop() as string;
		const firstName = parts.join(' ');
		return { firstName, lastName };
	}

	private escape(text: string): string {
		return (text || '')
			.replace(/\\/g, '\\\\')
			.replace(/\n/g, '\\n')
			.replace(/;/g, '\\;')
			.replace(/,/g, '\\,');
	}

	private toSafeFilename(name: string): string {
		return (name || 'contact')
			.trim()
			.replace(/\s+/g, '_')
			.replace(/[^a-zA-Z0-9._-]/g, '');
	}
}
